/**
 * Copyright 2024-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect } from "react";
import { When } from "react-if";
import { theme } from "@nordcloud/gnui";
import { ActionScope } from "~/generated/graphql";
import { LoaderWrap } from "~/components";
import { commaSeparated, isEmpty, isNotEmpty } from "~/tools";
import { useGetAllResourceGroups } from "~/views/resourceGroups/hooks";
import { SelectedAction } from "../types";

export function ResourceGroupSummary({ action }: { action: SelectedAction }) {
  const { getGroups, resourceGroups, loading } = useGetAllResourceGroups({
    basic: true,
  });

  const resourceGroupsToCreate =
    action.createResourceGroups?.map((group) => group.name) ?? [];
  const resourceGroupsNames = resourceGroups
    .filter((groups) => action?.resourceGroupIds?.includes(groups.id))
    .map(({ name }) => name);

  const allResourceGroupNames = [
    ...resourceGroupsNames,
    ...resourceGroupsToCreate,
  ];

  useEffect(() => {
    if (action.scope === ActionScope.Plan) {
      return;
    }
    if (isNotEmpty(action.resourceGroupIds ?? [])) {
      getGroups();
    }
  }, [getGroups, action.resourceGroupIds, action.scope]);
  return (
    <>
      {action.scope !== ActionScope.Plan &&
      !loading &&
      isEmpty(allResourceGroupNames) ? (
        <div css={{ color: theme.color.text.text03 }}>
          Resource groups not assigned
        </div>
      ) : (
        <>
          <When condition={isNotEmpty(resourceGroupsNames)}>
            <LoaderWrap loading={loading} inContent>
              {
                <div
                  css={{
                    marginBottom: theme.spacing.spacing03,
                    wordBreak: "break-word",
                  }}
                >
                  {commaSeparated(resourceGroupsNames)}
                </div>
              }
            </LoaderWrap>
          </When>

          <div css={{ wordBreak: "break-word" }}>
            {commaSeparated(resourceGroupsToCreate)}
          </div>
        </>
      )}
      <When condition={action.scope === ActionScope.Plan}>
        <div css={{ color: theme.color.text.text03 }}>
          There are no resource groups for Plan based action scope
        </div>
      </When>
    </>
  );
}
